import {
  UseMutationOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';

import { TId } from '@/features/common';
import { useNotifications } from '@/features/notifications';

import {
  createAutomation,
  deleteAutomation,
  fetchAutomations,
  updateAutomation,
} from '../api';
import { useAutomationsFeatureFlag } from '../hooks';
import { EAutomationType, IAutomation } from '../types';

export const AUTOMATIONS_QUERY_KEY = ['automations'];

export const useAutomationsQuery = <TSelect = IAutomation[]>(
  params: {
    workspaceId: TId;
    jobOpeningId: TId;
  },
  options?: {
    enabled?: boolean;
    select?: (data: IAutomation[]) => TSelect;
  }
) => {
  const isAutomationsFeatureEnabled = useAutomationsFeatureFlag();

  return useQuery<IAutomation[], Error, TSelect>({
    queryKey: [...AUTOMATIONS_QUERY_KEY, params.jobOpeningId],
    queryFn: () => fetchAutomations(params),
    enabled: isAutomationsFeatureEnabled && (options?.enabled ?? true),
    select: options?.select,
  });
};

export const useCreateAutomationMutation = (options?: UseMutationOptions) => {
  const queryClient = useQueryClient();
  const { addNotification } = useNotifications();

  return useMutation<
    IAutomation,
    Error,
    {
      workspaceId: TId;
      jobOpeningId: TId;
      stageId: TId;
      details: {
        type: EAutomationType;
        enabled: boolean;
        destinationStageId: TId | null;
        templateId: TId | null;
        systemTemplateId: TId | null;
        value: number | null;
      };
    }
  >({
    mutationFn: createAutomation,
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: [...AUTOMATIONS_QUERY_KEY, variables.jobOpeningId],
      });
      addNotification({
        type: 'automation_action_success',
        meta: { action: 'enabled' },
      });
    },
    ...(options as any),
  });
};

export const useUpdateAutomationMutation = (options?: UseMutationOptions) => {
  const queryClient = useQueryClient();
  const { addNotification } = useNotifications();

  return useMutation<
    IAutomation,
    Error,
    {
      workspaceId: TId;
      jobOpeningId: TId;
      stageId: TId;
      automationId: TId;
      details: {
        enabled: boolean;
        destinationStageId: TId | null;
        templateId: TId | null;
        systemTemplateId: TId | null;
        value: number | null;
      };
    }
  >({
    mutationFn: updateAutomation,
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: [...AUTOMATIONS_QUERY_KEY, variables.jobOpeningId],
      });
      addNotification({
        type: 'automation_action_success',
        meta: { action: 'updated' },
      });
    },
    ...(options as any),
  });
};

export const useDeleteAutomationMutation = (options?: UseMutationOptions) => {
  const queryClient = useQueryClient();
  const { addNotification } = useNotifications();

  return useMutation<
    void,
    Error,
    {
      workspaceId: TId;
      jobOpeningId: TId;
      stageId: TId;
      automationId: TId;
    }
  >({
    mutationFn: deleteAutomation,
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: [...AUTOMATIONS_QUERY_KEY, variables.jobOpeningId],
      });
      addNotification({
        type: 'automation_action_success',
        meta: { action: 'deleted' },
      });
    },
    ...(options as any),
  });
};
